import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import AttendanceReport from './pages/AttendanceReport';
import UploadSalaryExcel from './pages/payroll/UploadSalaryExcel';
import SalaryStatement from './pages/payroll/SalaryStatement';
import BankLetterView from './pages/payroll/BankLetterView';
import ConfigureSalary from './pages/payroll/ConfigureSalary';
import PayrollNotes from './pages/payroll/PayrollNotes';
import ServiceTasksOverview from './pages/folksaptapur/ServiceTasksOverview';
import MorningSadhana from './pages/folksaptapur/MorningSadhana';
import SadhanaReport from './pages/folksaptapur/SadhanaReport';
import AssignServiceTasks from './pages/folksaptapur/AssignServiceTasks';
import SalaryAdvancePage from './pages/salaryadvance/SalaryAdvancePage';
import VendorPaymentsPage from './pages/vendorpayments/VendorPaymentsPage';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#ff005a',
    },
  },
});

const PrivateRoute = ({ children, requiresPermission }) => {
  const { user, loading, hasAnyPermission } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // is_system_role (Admin) bypasses this via hasAnyPermission itself, same
  // as every backend require_permission check.
  if (requiresPermission && !hasAnyPermission(requiresPermission)) {
    return <Navigate to="/" />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/attendance"
        element={
          <PrivateRoute requiresPermission="attendance">
            <AttendanceReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute requiresPermission="admin">
            <AdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/payroll"
        element={
          <PrivateRoute requiresPermission="payroll.generate">
            <UploadSalaryExcel />
          </PrivateRoute>
        }
      />
      <Route
        path="/payroll/report"
        element={
          <PrivateRoute requiresPermission="payroll.generate">
            <SalaryStatement />
          </PrivateRoute>
        }
      />
      <Route
        path="/payroll/bank-letter"
        element={
          <PrivateRoute requiresPermission="payroll.generate">
            <BankLetterView />
          </PrivateRoute>
        }
      />
      <Route
        path="/payroll/salary-config"
        element={
          <PrivateRoute requiresPermission="payroll.configure_salary">
            <ConfigureSalary />
          </PrivateRoute>
        }
      />
      <Route
        path="/payroll/notes"
        element={
          <PrivateRoute requiresPermission="payroll.notes">
            <PayrollNotes />
          </PrivateRoute>
        }
      />
      <Route
        path="/folk-saptapur"
        element={
          <PrivateRoute requiresPermission="folk_saptapur.service_tasks">
            <ServiceTasksOverview />
          </PrivateRoute>
        }
      />
      <Route
        path="/folk-saptapur/morning-sadhana"
        element={
          <PrivateRoute requiresPermission="folk_saptapur.morning_sadhana">
            <MorningSadhana />
          </PrivateRoute>
        }
      />
      <Route
        path="/folk-saptapur/sadhana-report"
        element={
          <PrivateRoute requiresPermission="folk_saptapur.sadhana_report">
            <SadhanaReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/folk-saptapur/assign-tasks"
        element={
          <PrivateRoute requiresPermission="folk_saptapur.assign_tasks">
            <AssignServiceTasks />
          </PrivateRoute>
        }
      />
      <Route
        path="/salary-advance"
        element={
          <PrivateRoute requiresPermission="salary_advance.">
            <SalaryAdvancePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/vendor-payments"
        element={
          <PrivateRoute requiresPermission="vendor_payments.manage">
            <VendorPaymentsPage />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
